import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '@/i18n/locale-context';
import { readRecentSearches } from '@/lib/recent-searches';
import { Button, Input, Modal } from '@/components/ui';

/**
 * The search overlay — Phase 10's task list: "an overlay on desktop and a full-screen
 * sheet on mobile, with keyboard shortcut `/` and Escape to close."
 *
 * It is the `Modal` primitive with a wider panel, so Escape, the focus trap and the
 * return of focus all come from one implementation rather than a second copy. Submitting
 * navigates to the search page, which is where the filters, the sorts and the shareable
 * URL live — the overlay is a way in, not a second search UI.
 */
export function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, path } = useLocale();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<string[]>([]);

  useEffect(() => {
    if (open) setRecent(readRecentSearches());
  }, [open]);

  const go = (value: string) => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    onClose();
    setQuery('');
    navigate(`${path('search')}?q=${encodeURIComponent(trimmed)}`);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    go(query);
  };

  return (
    <Modal open={open} onClose={onClose} title={t('search.title')} className="max-w-2xl">
      <form role="search" onSubmit={onSubmit} className="flex flex-col gap-3">
        <Input
          label={t('search.title')}
          labelHidden
          type="search"
          // Read by the dialog's focus trap — see `useFocusTrap` in `lib/dom.ts`.
          data-autofocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('search.placeholder')}
        />
        <Button type="submit">{t('action.search')}</Button>
      </form>

      {recent.length > 0 && (
        <div className="mt-5">
          <h3 className="mb-2 text-xs font-extrabold uppercase tracking-wide text-ink">
            {t('search.recent')}
          </h3>
          <ul className="flex flex-wrap gap-2">
            {recent.map((item) => (
              <li key={item}>
                <button
                  type="button"
                  onClick={() => go(item)}
                  className="rounded-md border border-line px-3 py-1.5 text-xs font-semibold text-body transition hover:border-red hover:text-red"
                >
                  {item}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
